import React, { useEffect, useRef, useState } from "react";
import { drawFlightPath } from "./FlightPathOverlay";
import type { FlightRoute } from "./FlightPathOverlay";
import {
    getRenderedYardImage,
    getParkingData,
    getTrailerImage
} from "../../services/yard.service";
import { getTrailerAssignments } from "./trailerData";
import type { TrailerAssignment } from "./trailerData";
import type { TrailerImageName } from "../../services/yard.service";
import {
    getParkingCorners,
    polygonCenter,
    polygonLongAxis
} from "./trailerGeometry";
import type { ParkingGeometry } from "./trailerGeometry";

type Point = [number, number];

export interface YardSetup {
    georef?: {
        H: number[][];
        origin: {
            lat: number;
            lon: number;
        };
    };
    pod?: {
        home_px?: [number, number];
        home_ll?: [number, number];
    } | null;
    image?: {
        width: number;
        height: number;
    };
    geofence?: {
        px: Point[];
    };
    parking_spaces?: unknown[];
    buildings?: unknown[];
    blocks?: unknown[];
    no_parking_zones?: unknown[];
    sections?: unknown[];
    crosswalks?: unknown[];
    scale?: {
        meters_per_px: number;
    } | null;
}

export interface SelectedTrailer {
    number: string;
    type: string;
    length: number;
    skirts: 'yes' | 'no' | null;
}

interface Props {
    onTrailerClick?: (trailer: SelectedTrailer) => void;
    setupData: YardSetup;
    flightRoute?: FlightRoute | null;
}

export default function YardCanvas({ setupData, flightRoute, onTrailerClick }: Props): React.ReactElement {
    const pointerStartRef = useRef<{
        x: number;
        y: number;
    } | null>(null);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const trailerHitboxesRef = useRef<Array<{
        trailerNumber: string;
        trailerType: string;
        trailerLength: number;
        trailerSkirts: 'yes' | 'no' | null;
        x: number;
        y: number;
        width: number;
        height: number;
        angle: number;
    }>>([]);

    const [hoveredTrailer, setHoveredTrailer] = useState<{
        number: string;
        type: string;
        length: number;
        x: number;
        y: number;
    } | null>(null);

    const [yardImage, setYardImage] = useState<HTMLImageElement | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);

    const [trailerAssignments, setTrailerAssignments] =
        useState<TrailerAssignment[]>([]);

    useEffect(() => {
        let cancelled = false;
        let loading = false;

        const loadParking = async () => {
            if (cancelled || loading) return;
            loading = true;

            try {
                const response = await getParkingData();
                const assignments = getTrailerAssignments(
                    response.parking.parking_spaces ?? []
                );

                if (!cancelled) {
                    setTrailerAssignments(assignments);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error(
                        "Failed to refresh live parking assignments:",
                        error
                    );
                }
            } finally {
                loading = false;
            }
        };

        void loadParking();
        const interval = window.setInterval(() => {
            void loadParking();
        }, 5000);

        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    }, []);

    const [trailerImages, setTrailerImages] = useState<
        Partial<Record<TrailerImageName, HTMLImageElement>>
    >({});

    useEffect(() => {
        let cancelled = false;
        const objectUrls: string[] = [];

        const filenames: TrailerImageName[] = [
            "28 foot dry van.png",
            "28 foot intermodal.png",
            "28 foot reefer.png",
            "53 foot dry van.png",
            "53 foot intermodal.png",
            "53 foot reefer.png"
        ];

        const loadImages = async () => {
            const loaded: Partial<
                Record<TrailerImageName, HTMLImageElement>
            > = {};

            await Promise.all(
                filenames.map(async filename => {
                    try {
                        const url = await getTrailerImage(filename);
                        objectUrls.push(url);

                        const image = new Image();
                        image.src = url;
                        await image.decode();

                        loaded[filename] = image;
                    } catch (error) {
                        console.error(
                            `Failed to load trailer image ${filename}:`,
                            error
                        );
                    }
                })
            );

            if (!cancelled) {
                setTrailerImages(loaded);
                console.info(
                    "Trailer images loaded:",
                    Object.keys(loaded).length
                );
            }
        };

        void loadImages();

        return () => {
            cancelled = true;
            objectUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState<Point>([0, 0]);


    const dragRef = useRef<{
        x: number;
        y: number;
        offset: Point;
    } | null>(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;

        const loadImage = async () => {
            try {
                setImageError(null);

                const url = await getRenderedYardImage();
                objectUrl = url;

                const image = new Image();
                image.src = url;
                await image.decode();

                if (!cancelled) {
                    setYardImage(image);
                }
            } catch (error) {
                console.error("Failed to load rendered yard image:", error);

                if (!cancelled) {
                    setImageError("Rendered yard image is not available yet.");
                }
            }
        };

        void loadImage();

        return () => {
            cancelled = true;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;

        if (!canvas || !container) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const width = container.clientWidth;
        const height = container.clientHeight;

        if (width <= 0 || height <= 0) return;

        const dpr = window.devicePixelRatio || 1;

        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);

        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        ctx.fillStyle = "#b6b9b3";
        ctx.fillRect(0, 0, width, height);

        trailerHitboxesRef.current = [];
        if (!yardImage) return;

        const sourceWidth = yardImage.naturalWidth;
        const sourceHeight = yardImage.naturalHeight;

        const fit = Math.min(
            width / sourceWidth,
            height / sourceHeight
        ) * 0.9;

        ctx.translate(
            width / 2 + offset[0],
            height / 2 + offset[1]
        );

        ctx.scale(fit * zoom, fit * zoom);

        ctx.translate(
            -sourceWidth / 2,
            -sourceHeight / 2
        );

        ctx.drawImage(
            yardImage,
            0,
            0,
            sourceWidth,
            sourceHeight
        );

        // Match the Python renderer's world-to-image transformation.
        const geofence = setupData.geofence?.px ?? [];
        const worldPoints: Point[] = geofence.length >= 3
            ? geofence
            : [
                [0, 0],
                [
                    setupData.image?.width ?? sourceWidth,
                    setupData.image?.height ?? sourceHeight
                ]
            ];

        const xs = worldPoints.map(point => point[0]);
        const ys = worldPoints.map(point => point[1]);

        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const worldWidth = Math.max(1, maxX - minX);
        const worldHeight = Math.max(1, maxY - minY);
        const padding = 15;

        const usableWidth = sourceWidth - padding * 2;
        const usableHeight = sourceHeight - padding * 2;

        const worldScale = Math.max(
            0.01,
            Math.min(
                usableWidth / worldWidth,
                usableHeight / worldHeight
            )
        );

        const worldOffsetX =
            padding +
            (usableWidth - worldWidth * worldScale) / 2 -
            minX * worldScale;

        const worldOffsetY =
            padding +
            (usableHeight - worldHeight * worldScale) / 2 -
            minY * worldScale;

        const toImageX = (x: number) =>
            x * worldScale + worldOffsetX;

        const toImageY = (y: number) =>
            y * worldScale + worldOffsetY;

        const assignmentsBySpace = new Map<
            string,
            TrailerAssignment[]
        >();

        for (const assignment of trailerAssignments) {
            const key = assignment.parkingSpaceNumber;
            const group = assignmentsBySpace.get(key) ?? [];
            group.push(assignment);
            assignmentsBySpace.set(key, group);
        }

        for (const rawSpace of setupData.parking_spaces ?? []) {
            const space = rawSpace as ParkingGeometry;
            const spaceId = String(space.id ?? space.label ?? "");
            const assignments = assignmentsBySpace.get(spaceId);

            if (!assignments?.length) continue;

            const corners = getParkingCorners(space);
            if (corners.length < 3) continue;

            const center = polygonCenter(corners);
            const axis = polygonLongAxis(corners);

            const polygonWidth =
                Math.max(...corners.map(p => toImageX(p[0]))) -
                Math.min(...corners.map(p => toImageX(p[0])));

            const polygonHeight =
                Math.max(...corners.map(p => toImageY(p[1]))) -
                Math.min(...corners.map(p => toImageY(p[1])));

            for (const assignment of assignments) {
                const image = trailerImages[assignment.imageName];
                if (!image) continue;

                const lengthScale =
                    assignment.trailerLength === 28 ? 28 / 53 : 1;

                const slotOffset =
                    assignment.slotCount === 2
                        ? (
                            assignment.slotIndex -
                            (assignment.slotCount - 1) / 2
                        ) * axis.length * 0.46
                        : 0;

                const imageX = toImageX(
                    center[0] + axis.direction[0] * slotOffset
                );

                const imageY = toImageY(
                    center[1] + axis.direction[1] * slotOffset
                );

                const imageWidth = Math.max(
                    1,
                    polygonWidth * 0.92
                );

                const imageHeight = Math.max(
                    1,
                    polygonHeight * 0.92 * lengthScale
                );

                ctx.save();
                ctx.translate(imageX, imageY);

                // Equivalent to the Python PIL rotation:
                // rotate(angle + 270).
                ctx.rotate(axis.angle - Math.PI / 2);

                ctx.drawImage(
                    image,
                    -imageWidth / 2,
                    -imageHeight / 2,
                    imageWidth,
                    imageHeight
                );

                ctx.restore();

                const screenScale = fit * zoom;
                trailerHitboxesRef.current.push({
                    trailerNumber: assignment.trailerNumber,
                    trailerType: assignment.trailerType,
                    trailerLength: assignment.trailerLength,
                    trailerSkirts: assignment.trailerSkirts,
                    x: width / 2 + offset[0] +
                        (imageX - sourceWidth / 2) * screenScale,
                    y: height / 2 + offset[1] +
                        (imageY - sourceHeight / 2) * screenScale,
                    width: imageWidth * screenScale,
                    height: imageHeight * screenScale,
                    angle: axis.angle - Math.PI / 2
                });
            }
        }

        // Draw the optional flight route in yard coordinates.
        // The canvas already applies image fitting, zoom, and panning.
        if (flightRoute) {
            drawFlightPath(ctx, flightRoute, { toImageX, toImageY });
        }

    }, [
        setupData,
        yardImage,
        zoom,
        offset,
        trailerAssignments,
        trailerImages,
        flightRoute
    ]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new ResizeObserver(() => {
            setOffset((current) => [...current] as Point);
        });

        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden"
            style={{ minHeight: 400 }}
        >
            <canvas
                ref={canvasRef}
                className="block w-full h-full cursor-grab"
                onWheel={(event) => {
                    event.preventDefault();

                    const canvas = canvasRef.current;
                    const container = containerRef.current;
                    if (!canvas || !container || !yardImage) return;

                    const rect = canvas.getBoundingClientRect();
                    const mouseX = event.clientX - rect.left;
                    const mouseY = event.clientY - rect.top;

                    const width = container.clientWidth;
                    const height = container.clientHeight;

                    const fit = Math.min(
                        width / yardImage.naturalWidth,
                        height / yardImage.naturalHeight
                    ) * 0.9;

                    const nextZoom = Math.max(
                        0.25,
                        Math.min(
                            8,
                            zoom * (event.deltaY < 0 ? 1.1 : 0.9)
                        )
                    );

                    if (nextZoom === zoom) return;

                    const ratio = nextZoom / zoom;

                    setOffset(([ox, oy]) => [
                        mouseX - width / 2 -
                            (mouseX - width / 2 - ox) * ratio,
                        mouseY - height / 2 -
                            (mouseY - height / 2 - oy) * ratio,
                    ]);

                    setZoom(nextZoom);
                }}
                onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    event.preventDefault();
                    event.currentTarget.setPointerCapture(event.pointerId);
                    pointerStartRef.current = {
                        x: event.clientX,
                        y: event.clientY
                    };
                    event.currentTarget.style.cursor = "grabbing";
                    dragRef.current = {
                        x: event.clientX,
                        y: event.clientY,
                        offset
                    };
                }}
                onPointerMove={(event) => {
                    if (!dragRef.current) {
                        const rect = event.currentTarget.getBoundingClientRect();
                        const mouseX = event.clientX - rect.left;
                        const mouseY = event.clientY - rect.top;

                        const hit = [...trailerHitboxesRef.current]
                            .reverse()
                            .find((trailer) => {
                                const dx = mouseX - trailer.x;
                                const dy = mouseY - trailer.y;
                                const cos = Math.cos(trailer.angle);
                                const sin = Math.sin(trailer.angle);
                                const localX = dx * cos + dy * sin;
                                const localY = -dx * sin + dy * cos;

                                return Math.abs(localX) <= trailer.width / 2 &&
                                    Math.abs(localY) <= trailer.height / 2;
                            });

                        setHoveredTrailer(hit ? {
                            number: hit.trailerNumber,
                            type: hit.trailerType,
                            length: hit.trailerLength,
                            x: mouseX,
                            y: mouseY
                        } : null);
                        event.currentTarget.style.cursor = hit ? "pointer" : "grab";
                        return;
                    }

                    setHoveredTrailer(null);
                    setOffset([
                        dragRef.current.offset[0] +
                            event.clientX - dragRef.current.x,
                        dragRef.current.offset[1] +
                            event.clientY - dragRef.current.y
                    ]);
                }}
                onPointerUp={(event) => {
                    const start = pointerStartRef.current;
                    pointerStartRef.current = null;

                    const isClick = event.button === 0 &&
                        start !== null &&
                        Math.hypot(
                            event.clientX - start.x,
                            event.clientY - start.y
                        ) <= 5;

                    if (isClick && onTrailerClick) {
                        const rect = event.currentTarget.getBoundingClientRect();
                        const mouseX = event.clientX - rect.left;
                        const mouseY = event.clientY - rect.top;

                        const hit = [...trailerHitboxesRef.current]
                            .reverse()
                            .find((trailer) => {
                                const dx = mouseX - trailer.x;
                                const dy = mouseY - trailer.y;
                                const cos = Math.cos(trailer.angle);
                                const sin = Math.sin(trailer.angle);
                                const localX = dx * cos + dy * sin;
                                const localY = -dx * sin + dy * cos;

                                return Math.abs(localX) <= trailer.width / 2 &&
                                    Math.abs(localY) <= trailer.height / 2;
                            });

                        if (hit) {
                            onTrailerClick({
                                number: hit.trailerNumber,
                                type: hit.trailerType,
                                length: hit.trailerLength,
                                skirts: hit.trailerSkirts
                            });
                        }
                    }

                    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                    }
                    event.currentTarget.style.cursor = "grab";
                    dragRef.current = null;
                }}
                onPointerCancel={() => {
                    pointerStartRef.current = null;
                    dragRef.current = null;
                }}
                onPointerLeave={() => {
                    if (dragRef.current) return;
                    setHoveredTrailer(null);
                }}
            />

            {hoveredTrailer && (
                <div
                    className="absolute pointer-events-none z-50 rounded-md border-2 border-yellow-400 px-4 py-2 text-base font-bold shadow-xl"
                    style={{
                        left: Math.min(hoveredTrailer.x + 14, Math.max(0, (containerRef.current?.clientWidth ?? 0) - 160)),
                        top: Math.max(0, hoveredTrailer.y - 56),
                        backgroundColor: "#111827",
                        color: "#ffffff",
                        whiteSpace: "nowrap"
                    }}
                >
                    <div>Trailer Number: {hoveredTrailer.number}</div>
                    <div>Trailer Type: {hoveredTrailer.type.replace(/\b\w/g, char => char.toUpperCase())}</div>
                    <div>Trailer Length: {hoveredTrailer.length} ft</div>
                </div>
            )}

            {imageError && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-white/90 rounded-lg px-5 py-3 shadow text-sm text-gray-700">
                        {imageError}
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={() => {
                    setZoom(1);
                    setOffset([0, 0]);
                }}
                className="absolute bottom-3 right-3 bg-white px-3 py-2 rounded shadow text-sm"
            >
                Reset View
            </button>
        </div>
    );
}
